/**
 * f16.1: the right-hand stack of `dashboard.html` -- cycle preparation and
 * "Needs attention".
 *
 * Preparation is served only to the tier that prepares cycles
 * (BR-F16.1-7); everyone else gets the cycle's state and its next milestone.
 * The attention list carries only what the caller could act on
 * (BR-F16.1-8); `null` means "not eligible for any item" and renders nothing.
 */

import { Check, ChevronRight, ListChecks, RefreshCw } from 'lucide-react';
import { Link } from 'react-router-dom';

import type { DashboardPreparation, DashboardScoped } from '@/api/client';
import { formatDayMonth } from '@/routes/cycles/workflowView';

import { ATTENTION_LABEL, ATTENTION_PILL, formatCount, nextMilestone } from './dashboardView';

function stepClass(status: string, isCurrent: boolean): string {
  if (status === 'COMPLETED') return 'step is-done';
  if (status === 'STALE') return 'step is-stale';
  if (isCurrent) return 'step is-current';
  return 'step is-todo';
}

function StepNumber({ status, number }: { status: string; number: number }) {
  if (status === 'COMPLETED') return <Check aria-hidden="true" />;
  if (status === 'STALE') return <RefreshCw aria-hidden="true" />;
  return <>{number}</>;
}

function stepMeta(step: DashboardPreparation['steps'][number]): string | null {
  const parts = [
    step.completed_at ? `Committed ${formatDayMonth(step.completed_at)}` : null,
    step.summary_text,
  ].filter(Boolean);
  return parts.length ? parts.join(' · ') : step.status.name;
}

function PreparationRail({ cycleId, preparation }: { cycleId: string; preparation: DashboardPreparation }) {
  return (
    <div className="card">
      <div className="card-head">
        <h3>Cycle preparation</h3>
        <span className="hint">{`${preparation.completed_count} of ${preparation.total_steps} steps`}</span>
      </div>
      <div className="card-body dash-card-body">
        <progress
          className="dash-progress mb-4"
          value={preparation.completed_count}
          max={Math.max(preparation.total_steps, 1)}
          aria-label="Preparation progress"
        />
        <div className="step-rail dash-rail">
          {preparation.steps.map((step) => (
            <div key={step.code} className={stepClass(step.status.code, step.code === preparation.current_step_code)}>
              <span className="step-num"><StepNumber status={step.status.code} number={step.number} /></span>
              <div className="step-body">
                <div className="step-title">
                  {step.name}
                  {step.status.code === 'STALE' && <span className="badge pill-warning">Updates available</span>}
                </div>
                <div className="step-meta">{stepMeta(step)}</div>
              </div>
            </div>
          ))}
        </div>
        <Link className="btn btn-secondary btn-sm mt-4 dash-block" to={`/cycles/${cycleId}`}>
          <ListChecks aria-hidden="true" /> Open cycle workflow
        </Link>
      </div>
    </div>
  );
}

function CycleStatus({ body }: { body: DashboardScoped }) {
  const cycle = body.cycle!;
  const milestone = nextMilestone(cycle);
  return (
    <div className="card">
      <div className="card-head"><h3>Cycle status</h3></div>
      <div className="card-body dash-card-body">
        <p className="dash-status">
          {cycle.state.name}
          {milestone ? ` · ${milestone}` : ''}
        </p>
      </div>
    </div>
  );
}

export function PreparationCard({ body }: { body: DashboardScoped }) {
  if (!body.cycle) return null;
  if (body.preparation) return <PreparationRail cycleId={body.cycle.id} preparation={body.preparation} />;
  return <CycleStatus body={body} />;
}

export function AttentionCard({ body }: { body: DashboardScoped }) {
  if (body.attention === null || body.attention === undefined) return null;
  return (
    <div className="card">
      <div className="card-head"><h3>Needs attention</h3></div>
      <div className="card-body stack-sm dash-card-body">
        {body.attention.length === 0 ? (
          <p className="dash-empty">Nothing needs attention</p>
        ) : (
          body.attention.map((item) => (
            <Link key={item.code} className="row dash-attention-row" to={item.target}>
              <span className={`badge ${ATTENTION_PILL[item.code] ?? 'pill-neutral'}`}>{formatCount(item.count)}</span>
              <span className="dash-grow">{ATTENTION_LABEL[item.code] ?? item.code}</span>
              <ChevronRight className="dash-chevron" aria-hidden="true" />
            </Link>
          ))
        )}
      </div>
    </div>
  );
}
